// src/pages/buyers/EcommerceStore.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Badge,
  InputBase,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Rating,
  Container,
  Dialog,
  DialogContent,
  Slide,
  Snackbar,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Avatar,
  Paper,
  Skeleton,
  Pagination,
  useTheme,
  alpha,
  styled,
  Select,
  FormControl,
  useMediaQuery,
  Fab,
  CircularProgress,
  DialogActions,
  TextField,
  Fade,
  Backdrop,
  Stack,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
  ListItemIcon,
  InputLabel,
  MobileStepper,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Security as SecurityIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  ShoppingBag as ShoppingBagIcon,
  Storefront as StorefrontIcon,
  Verified as VerifiedIcon,
  WhatsApp as WhatsAppIcon,
  Science as ScienceIcon,
  Shield as ShieldIcon,
  Dashboard as DashboardIcon,
  Receipt as ReceiptIcon,
  Lock as LockIcon,
  PersonAddOutlined as PersonAddOutlinedIcon,
  ShoppingCart as CartCheckoutIcon,
  Category as CategoryIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  ListAlt as ListAltIcon,
  Home as HomeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  Whatshot as WhatshotIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import Preloader from '../../theme/Preloader';
import logo from '../../assets/images.png';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const BRAND_COLORS = {
  primary: '#2E7D32',
  primaryLight: '#4CAF50',
  primaryDark: '#1B5E20',
  secondary: '#C62828',
  accent: '#FF6F00',
  orange: '#F57C00',
  background: '#F5F7FA',
  surface: '#FFFFFF',
  text: '#1A1A1A',
  textSecondary: '#666666',
  border: '#E8ECF1',
  success: '#2E7D32',
  warning: '#ED6C02',
  error: '#D32F2F',
  whatsapp: '#25D366',
  info: '#0288D1',
};

const CURRENCY_SYMBOLS = {
  NGN: '₦',
  USD: '$',
  EUR: '€',
  GBP: '£',
  GHS: 'GH₵',
  KES: 'KSh',
  ZAR: 'R',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  PRODUCTS: {
    PUBLIC: `${API_BASE_URL}/products/public.php`,
    DETAIL: (id) => `${API_BASE_URL}/products/detail.php?id=${id}`,
  },
  ORDERS: {
    CREATE: `${API_BASE_URL}/orders/create.php`,
  },
};

// Category colors for visual distinction
const CATEGORY_COLORS = {
  'Acids': '#F57C00',
  'Solvents': '#1565C0',
  'Alkalis': '#00695C',
  'Salts': '#6A1B9A',
  'Glycols': '#AD1457',
  'Aromatics': '#283593',
  'Surfactants': '#F9A825',
  'Oxidizers': '#D32F2F',
  'Fertilizers': '#2E7D32',
  'Aldehydes': '#4E342E',
  'Pharmaceutical Ingredients': '#0288D1',
  'Energy & Battery': '#0D47A1',
  'Water Treatment': '#00897B',
  'Food & Beverages': '#558B2F',
  'Agriculture': '#33691E',
  'Manufacturing': '#4E342E',
  'Electronics & Semiconductor': '#1A237E',
  'Personal Care & Cosmetics': '#AD1457',
  'default': '#78909C',
};

const getCategoryColor = (category) => {
  if (!category) return CATEGORY_COLORS.default;
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
};

// Product classification by chemical type
const PRODUCT_CLASSIFICATIONS = {
  ACID: { label: 'Acids', icon: '🔬', color: '#F57C00', keywords: ['acid', 'sulfuric', 'hydrochloric', 'nitric', 'phosphoric', 'acetic', 'citric'] },
  BASE: { label: 'Bases / Alkalis', icon: '🧪', color: '#00695C', keywords: ['sodium hydroxide', 'caustic soda', 'potassium hydroxide', 'ammonia', 'lime'] },
  SALT: { label: 'Salts', icon: '🧂', color: '#6A1B9A', keywords: ['sodium chloride', 'potassium', 'calcium', 'magnesium', 'sulfate', 'nitrate'] },
  SOLVENT: { label: 'Solvents', icon: '💧', color: '#1565C0', keywords: ['acetone', 'methanol', 'ethanol', 'toluene', 'xylene', 'benzene'] },
  OXIDIZER: { label: 'Oxidizers', icon: '🔥', color: '#D32F2F', keywords: ['peroxide', 'permanganate', 'chlorate', 'nitrate', 'oxygen'] },
  SURFACTANT: { label: 'Surfactants', icon: '🫧', color: '#F9A825', keywords: ['surfactant', 'detergent', 'emulsifier', 'wetting agent'] },
  GLYCOL: { label: 'Glycols', icon: '🧊', color: '#AD1457', keywords: ['ethylene glycol', 'propylene glycol', 'glycol'] },
  FERTILIZER: { label: 'Fertilizers', icon: '🌱', color: '#2E7D32', keywords: ['urea', 'ammonium', 'phosphate', 'potash', 'nitrogen'] },
  PHARMACEUTICAL: { label: 'Pharmaceuticals', icon: '💊', color: '#0288D1', keywords: ['api', 'active', 'pharma', 'medicinal'] },
  AGRICULTURE: { label: 'Agricultural', icon: '🌾', color: '#33691E', keywords: ['pesticide', 'herbicide', 'fungicide', 'agriculture'] },
  WATER_TREATMENT: { label: 'Water Treatment', icon: '💧', color: '#00897B', keywords: ['chlorine', 'flocculant', 'coagulant', 'water treatment'] },
  FOOD: { label: 'Food & Beverage', icon: '🍽️', color: '#558B2F', keywords: ['food grade', 'beverage', 'edible', 'food'] },
  ELECTRONICS: { label: 'Electronics', icon: '⚡', color: '#1A237E', keywords: ['semiconductor', 'electronic', 'battery', 'circuit'] },
  INDUSTRIAL: { label: 'Industrial', icon: '🏭', color: '#4E342E', keywords: ['industrial', 'manufacturing', 'processing'] },
  UNKNOWN: { label: 'Other', icon: '📦', color: '#78909C', keywords: [] },
};

const classifyProduct = (product) => {
  const searchText = [
    product.name,
    product.productType,
    product.description,
    product.ChemicalFormula,
    product.Grade,
    product.category,
    product.applications,
  ].filter(Boolean).join(' ').toLowerCase();

  for (const [key, classification] of Object.entries(PRODUCT_CLASSIFICATIONS)) {
    if (key === 'UNKNOWN') continue;
    if (classification.keywords.some(keyword => searchText.includes(keyword.toLowerCase()))) {
      return key;
    }
  }
  return 'UNKNOWN';
};

const getClassificationColor = (classificationKey) => {
  return PRODUCT_CLASSIFICATIONS[classificationKey]?.color || '#78909C';
};

const getClassificationLabel = (classificationKey) => {
  return PRODUCT_CLASSIFICATIONS[classificationKey]?.label || 'Other';
};

const getClassificationIcon = (classificationKey) => {
  return PRODUCT_CLASSIFICATIONS[classificationKey]?.icon || '📦';
};

// Sample product specifications
const getProductSpecifications = (product) => {
  const defaultSpecs = [
    { label: 'Purity', value: product?.Grade || 'Standard' },
    { label: 'Formula', value: product?.ChemicalFormula || 'N/A' },
    { label: 'Appearance', value: 'Varies' },
    { label: 'Packaging', value: 'As per standard' },
    { label: 'Grade', value: product?.Grade || 'Industrial' },
    { label: 'Origin', value: product?.CountryOrigin || 'Various' },
  ];
  return defaultSpecs;
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[ECOMMERCE] ℹ️ ${message}`, data || ''),
  error: (message, error) => console.error(`[ECOMMERCE] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[ECOMMERCE] 🐛 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[ECOMMERCE] ⚠️ ${message}`, data || ''),
  success: (message, data) => console.log(`[ECOMMERCE] ✅ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const decodeHtmlEntities = (text) => {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const formatCurrency = (amount, currency = 'NGN') => {
  if (!amount && amount !== 0) return 'N/A';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const formatted = Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const sanitizeInput = (input) => {
  if (!input) return '';
  return input.replace(/[<>]/g, '').trim();
};

// ============================================
// STYLED COMPONENTS
// ============================================

const SearchBar = styled(Paper)(({ theme }) => ({
  padding: '2px 12px',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 500,
  background: '#FFFFFF',
  borderRadius: 25,
  border: '1px solid #E8ECF1',
  transition: 'all 0.3s ease',
  '&:hover': { borderColor: BRAND_COLORS.primary },
  '&:focus-within': {
    borderColor: BRAND_COLORS.primary,
    boxShadow: `0 0 0 3px ${alpha(BRAND_COLORS.primary, 0.15)}`,
  },
}));

const CategoryNavItem = styled(Box)(({ theme, selected, color }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 20,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: selected ? color || BRAND_COLORS.primary : '#F5F7FA',
  color: selected ? '#FFFFFF' : 'text.primary',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  fontSize: '0.8rem',
  fontWeight: selected ? 600 : 400,
  border: selected ? 'none' : '1px solid transparent',
  '&:hover': {
    backgroundColor: selected ? (color || BRAND_COLORS.primaryDark) : '#E8ECF1',
    transform: 'translateY(-1px)',
  },
  '& .count': {
    fontSize: '0.65rem',
    opacity: 0.8,
    marginLeft: 2,
  },
}));

const ProductCardStyled = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  background: 'white',
  border: '1px solid #E8ECF1',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.10)',
    borderColor: BRAND_COLORS.primary,
    '& .product-image': { transform: 'scale(1.03)' },
  },
}));

const ProductImage = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '75%',
  overflow: 'hidden',
  backgroundColor: '#F5F7FA',
  '& img': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

// ============================================
// AUTH REQUIRED DIALOG
// ============================================

const AuthRequiredDialog = React.memo(({ open, onClose, onLogin, onSignup, action = 'send enquiry' }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionComponent={Fade}>
    <Box sx={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryDark})`, p: 4, textAlign: 'center' }}>
      <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <LockIcon sx={{ fontSize: 40, color: 'white' }} />
      </Box>
      <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>Authentication Required</Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>Please log in to {action}</Typography>
    </Box>
    <DialogContent sx={{ p: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>You need to be signed in to continue.</Typography>
      </Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3, justifyContent: 'center' }}>
        {[
          { icon: <SecurityIcon />, label: 'Secure' },
          { icon: <VerifiedIcon />, label: 'Verified' },
          { icon: <LocalShippingIcon />, label: 'Fast Delivery' },
        ].map((item, idx) => (
          <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5, bgcolor: 'rgba(46,125,50,0.04)', borderRadius: 2, flex: 1 }}>
            <Box sx={{ color: BRAND_COLORS.primary, mb: 0.5 }}>{item.icon}</Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{item.label}</Typography>
          </Box>
        ))}
      </Stack>
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button variant="outlined" fullWidth onClick={onSignup} sx={{ borderRadius: 25, textTransform: 'none', fontWeight: 600, borderColor: BRAND_COLORS.primary, color: BRAND_COLORS.primary }} startIcon={<PersonAddOutlinedIcon />}>Create Account</Button>
        <Button variant="contained" fullWidth onClick={onLogin} sx={{ borderRadius: 25, textTransform: 'none', fontWeight: 600, bgcolor: BRAND_COLORS.primary }} startIcon={<LoginIcon />}>Log In</Button>
      </Box>
    </DialogContent>
    <IconButton onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12, color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}><CloseIcon /></IconButton>
  </Dialog>
));

AuthRequiredDialog.displayName = 'AuthRequiredDialog';

// ============================================
// CONTACT DIALOG (Replaces EnquiryDialog)
// ============================================

// ============================================
// CONTACT DIALOG (Replaces EnquiryDialog)
// ============================================

const ContactDialog = React.memo(({ open, onClose, product, onSubmit, isAuthenticated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    attachments: [], // Array of file objects
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && product) {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          setFormData(prev => ({
            ...prev,
            name: user.email?.split('@')[0] || 'User',
            email: user.email || '',
            phone: user.phone || '',
          }));
        } catch (e) {}
      }
    }
    // Reset attachments when dialog closes
    return () => {
      if (!open) {
        // Cleanup preview URLs
        previewUrls.forEach(url => URL.revokeObjectURL(url));
        setPreviewUrls([]);
        setFormData(prev => ({ ...prev, attachments: [] }));
      }
    };
  }, [open, product]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFileAttach = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate file types (images only)
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        attachments: 'Please upload only image files (JPEG, PNG, GIF, WEBP, SVG)'
      }));
      return;
    }

    // Validate file size (max 5MB each)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      setErrors(prev => ({
        ...prev,
        attachments: 'Image files must be less than 5MB'
      }));
      return;
    }

    // Create preview URLs
    const newPreviewUrls = files.map(file => URL.createObjectURL(file));
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
    setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
    setErrors(prev => ({ ...prev, attachments: null }));
  };

  const handleRemoveAttachment = (index) => {
    // Revoke the URL to avoid memory leaks
    URL.revokeObjectURL(previewUrls[index]);
    
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      // Simulate file input change
      const fakeEvent = { target: { files: event.dataTransfer.files } };
      handleFileAttach(fakeEvent);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setLoading(true);
    // Pass form data including attachments to parent
    onSubmit(formData);
  };

  if (!product) return null;

  const MAX_ATTACHMENTS = 5;
  const remainingSlots = MAX_ATTACHMENTS - formData.attachments.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionComponent={Slide}>
      <Box sx={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryDark})`, p: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>Contact Supplier</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>{product.name}</Typography>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Your Name"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={handleChange('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange('message')}
              error={!!errors.message}
              helperText={errors.message}
              required
              placeholder="I'm interested in this product..."
            />
          </Grid>

          {/* Image Attachment Section */}
          <Grid item xs={12}>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Attach Images (Optional)
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({formData.attachments.length}/{MAX_ATTACHMENTS} - Max 5MB each)
                </Typography>
              </Typography>

              {/* Drop Zone */}
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={{
                  border: `2px dashed ${errors.attachments ? BRAND_COLORS.error : '#E8ECF1'}`,
                  borderRadius: 3,
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  bgcolor: '#FAFAFA',
                  '&:hover': {
                    borderColor: BRAND_COLORS.primary,
                    bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                  },
                  ...(formData.attachments.length >= MAX_ATTACHMENTS && {
                    opacity: 0.5,
                    pointerEvents: 'none',
                  }),
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileAttach}
                  style={{ display: 'none' }}
                  disabled={formData.attachments.length >= MAX_ATTACHMENTS}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: alpha(BRAND_COLORS.primary, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0.5,
                    }}
                  >
                    <AddIcon sx={{ color: BRAND_COLORS.primary }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {formData.attachments.length >= MAX_ATTACHMENTS
                      ? 'Maximum attachments reached'
                      : 'Click or drag & drop images here'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    JPEG, PNG, GIF, WEBP, SVG • Max 5MB each
                  </Typography>
                  {remainingSlots > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {remainingSlots} slot{remainingSlots > 1 ? 's' : ''} remaining
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Error Message */}
              {errors.attachments && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.attachments}
                </Typography>
              )}

              {/* Attachment Previews */}
              {previewUrls.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                      gap: 1,
                    }}
                  >
                    {previewUrls.map((url, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px solid #E8ECF1',
                          '&:hover': {
                            borderColor: BRAND_COLORS.error,
                          },
                          '&:hover .delete-btn': {
                            opacity: 1,
                          },
                        }}
                      >
                        <img
                          src={url}
                          alt={`Attachment ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                        <IconButton
                          className="delete-btn"
                          size="small"
                          onClick={() => handleRemoveAttachment(index)}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            width: 20,
                            height: 20,
                            '&:hover': {
                              bgcolor: BRAND_COLORS.error,
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                        <Chip
                          label={`${index + 1}`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            bottom: 2,
                            left: 2,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            height: 16,
                            fontSize: '0.5rem',
                            '& .MuiChip-label': { fontSize: '0.5rem', px: 0.5 },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* File Info */}
              {formData.attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formData.attachments.length} image{formData.attachments.length > 1 ? 's' : ''} attached
                    {' • '}
                    Total size: {formData.attachments.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024 > 1
                      ? `${(formData.attachments.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(1)} MB`
                      : `${Math.round(formData.attachments.reduce((sum, file) => sum + file.size, 0) / 1024)} KB`
                    }
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: BRAND_COLORS.primary }}
        >
          {loading ? <CircularProgress size={20} /> : 'Send Message'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

ContactDialog.displayName = 'ContactDialog';

// ============================================
// PRODUCT DETAIL DIALOG
// ============================================

const ProductDetailDialog = React.memo(({
  open,
  product,
  onClose,
  onEnquiry,
  isInWishlist,
  onToggleWishlist,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [specifications, setSpecifications] = useState([]);

  useEffect(() => {
    if (open && product) {
      fetchProductDetail(product.id);
    }
  }, [open, product]);

  const fetchProductDetail = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.PRODUCTS.DETAIL(id));
      const result = await response.json();
      if (result.success) {
        const productData = result.data.product;
        productData.supplier_email = productData.supplier_email || null;
        productData.supplier_name = productData.supplier_name || productData.ManufacturerName || 'Supplier';
        setProductDetail(productData);
        setRelatedProducts(result.data.related_products || []);
        setSpecifications(getProductSpecifications(productData));
      }
    } catch (error) {
      logger.error('Error fetching product detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const formatPrice = useCallback((amount, currency) => {
    if (!amount) return 'N/A';
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol}${Number(amount).toLocaleString()}`;
  }, []);

  if (!product) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Slide}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          maxHeight: '92vh',
          background: '#FFFFFF',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      position: 'relative',
                      bgcolor: '#F8F9FA',
                      borderRadius: 3,
                      overflow: 'hidden',
                      height: { xs: 250, sm: 300, md: 320 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #E8ECF1',
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl(product.image)}
                      alt={product.name}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        p: 2,
                      }}
                      onError={(e) => {
                        e.target.src = '';
                        e.target.style.display = 'none';
                      }}
                    />
                    {product.discounts > 0 && (
                      <Chip
                        label={`${product.discounts}% OFF`}
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          bgcolor: BRAND_COLORS.accent,
                          color: 'white',
                          fontWeight: 700,
                        }}
                      />
                    )}
                    {product.classification && (
                      <Chip
                        icon={<Box component="span" sx={{ fontSize: '0.8rem' }}>{getClassificationIcon(product.classification)}</Box>}
                        label={getClassificationLabel(product.classification)}
                        sx={{
                          position: 'absolute',
                          bottom: 12,
                          left: 12,
                          bgcolor: getClassificationColor(product.classification),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          height: 24,
                        }}
                      />
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <StorefrontIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {productDetail?.supplier_name || product.ManufacturerName || 'Verified Supplier'}
                      </Typography>
                      {productDetail?.supplier_verified && (
                        <VerifiedIcon sx={{ fontSize: 14, color: BRAND_COLORS.primary }} />
                      )}
                    </Box>

                    <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                      {product.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary" fontFamily="monospace" sx={{ mb: 1 }}>
                      CAS: {product.registryNumber || 'N/A'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating value={product.average_rating || 0} precision={0.5} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary">
                        ({product.total_reviews || 0} reviews)
                      </Typography>
                      <Chip
                        size="small"
                        label={product.quantity_in_stock > 0 ? 'In Stock' : 'Out of Stock'}
                        sx={{
                          bgcolor: product.quantity_in_stock > 0 ? BRAND_COLORS.success : BRAND_COLORS.error,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      {product.classification && (
                        <Chip
                          size="small"
                          icon={<Box component="span" sx={{ fontSize: '0.7rem' }}>{getClassificationIcon(product.classification)}</Box>}
                          label={getClassificationLabel(product.classification)}
                          sx={{
                            bgcolor: getClassificationColor(product.classification),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.6rem',
                            height: 20,
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {/* <Typography variant="h4" fontWeight={700} color="primary">
                        {formatCurrency(product.discountedPrice || product.price, product.currency)}
                      </Typography> */}
                      {product.discounts > 0 && (
                        <Typography variant="body1" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          {formatCurrency(product.price, product.currency)}
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                      {product.description || 'No description available'}
                    </Typography>

                 <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3 }}>
  {product.ChemicalFormula && (
    <Box>
      <Typography variant="caption" color="text.secondary">Formula</Typography>
      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
        {product.ChemicalFormula}
      </Typography>
    </Box>
  )} {product.hs_code && (
    <Box>
      <Typography variant="caption" color="text.secondary">HS Code</Typography>
      <Typography
        variant="body2"
        fontFamily="monospace"
        fontWeight={700}
        sx={{
          color: BRAND_COLORS.primaryDark,
          bgcolor: alpha(BRAND_COLORS.primary, 0.08),
          px: 1,
          py: 0.3,
          borderRadius: 1,
          display: 'inline-block',
        }}
      >
        {product.hs_code}
      </Typography>
    </Box>
  )}
  {product.Grade && (
    <Box>
      <Typography variant="caption" color="text.secondary">Grade</Typography>
      <Typography variant="body2" fontWeight={500}>{product.Grade}</Typography>
    </Box>
  )}
                      {product.Grade && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Grade</Typography>
                          <Typography variant="body2" fontWeight={500}>{product.Grade}</Typography>
                        </Box>
                      )}
                      {product.applications && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Application</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {decodeHtmlEntities(product.applications)}
                          </Typography>
                        </Box>
                      )}
                      {product.productType && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Type</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {decodeHtmlEntities(product.productType)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => onEnquiry(product)}
                        sx={{
                          borderRadius: 25,
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: BRAND_COLORS.primary,
                          '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
                          py: 1.2,
                        }}
                        startIcon={<SendIcon />}
                      >
                        Send Enquiry
                      </Button>
                      <IconButton
                        onClick={() => onToggleWishlist(product)}
                        sx={{
                          border: '2px solid #E8ECF1',
                          borderRadius: 2,
                          width: 48,
                          height: 48,
                          '&:hover': {
                            borderColor: BRAND_COLORS.primary,
                            bgcolor: alpha(BRAND_COLORS.primary, 0.05),
                          },
                        }}
                      >
                        {isInWishlist(product.id) ? (
                          <FavoriteIcon sx={{ color: BRAND_COLORS.secondary }} />
                        ) : (
                          <FavoriteBorderIcon />
                        )}
                      </IconButton>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalShippingIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">Fast Delivery</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SecurityIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">Verified Supplier</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ShieldIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">Quality Assured</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ borderTop: '1px solid #E8ECF1', bgcolor: '#F8F9FA' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  px: { xs: 2, sm: 3, md: 4 },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    minHeight: 48,
                  },
                }}
              >
                <Tab label="Specifications" icon={<ListAltIcon />} iconPosition="start" />
                <Tab label="Product Details" icon={<DescriptionIcon />} iconPosition="start" />
                <Tab label="Supplier Info" icon={<StorefrontIcon />} iconPosition="start" />
              </Tabs>

              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: '#FFFFFF' }}>
                {activeTab === 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table>
                      <TableBody>
                        {specifications.map((spec, idx) => (
                          <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { bgcolor: '#FAFAFA' } }}>
                            <TableCell sx={{ fontWeight: 600, width: '30%', color: 'text.secondary' }}>
                              {spec.label}
                            </TableCell>
                            <TableCell>{spec.value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {activeTab === 1 && (
                  <Box>
                    <Typography variant="body2" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                      {product.description || 'No detailed description available.'}
                    </Typography>
                    <Box
                      sx={{
                        mt: 2,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {[
                        { label: 'CAS Number', value: product.registryNumber || 'N/A' },
                        { label: 'Chemical Formula', value: product.ChemicalFormula || 'N/A' },
                        { label: 'Grade', value: product.Grade || 'N/A' },
                        { label: 'Manufacturer', value: product.ManufacturerName || 'N/A' },
                        { label: 'Country of Origin', value: product.CountryOrigin || 'N/A' },
                        { label: 'Unit of Measure', value: product.unit_of_measure || 'kg' },
                        { label: 'Min Order Qty', value: product.min_order_quantity || '1' },
                        { label: 'Delivery Time', value: product.DeliveryTime || 'Contact supplier' },
                      ].map((item) => (
                        <Box key={item.label} sx={{ p: 1.5, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                          <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: BRAND_COLORS.primary, width: 56, height: 56 }}>
                        <StorefrontIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {productDetail?.supplier_name || product.ManufacturerName || 'Verified Supplier'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {productDetail?.supplier_verified && (
                            <Chip label="Verified" size="small" sx={{ bgcolor: BRAND_COLORS.primary, color: 'white' }} />
                          )}
                          <Typography variant="caption" color="text.secondary">Member since 2020</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      {[
                        { label: 'Company', value: productDetail?.supplier_name || product.ManufacturerName || 'N/A' },
                        { label: 'Email', value: productDetail?.supplier_email || 'contact@supplier.com' },
                        { label: 'Phone', value: productDetail?.supplier_phone || 'Contact supplier' },
                        { label: 'Address', value: productDetail?.supplier_address || 'N/A' },
                      ].map((item) => (
                        <Box key={item.label} sx={{ p: 1.5, bgcolor: '#F8F9FA', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                          <Typography variant="body2" fontWeight={500}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="outlined"
                        startIcon={<SendIcon />}
                        sx={{ borderRadius: 25, textTransform: 'none' }}
                        onClick={() => onEnquiry(product)}
                      >
                        Contact Supplier
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {relatedProducts.length > 0 && (
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, borderTop: '1px solid #E8ECF1' }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Related Products
                </Typography>
                <Grid container spacing={2}>
                  {relatedProducts.slice(0, 4).map((relProduct) => (
                    <Grid item xs={6} sm={4} md={3} key={relProduct.id}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          },
                        }}
                        onClick={() => {
                          onClose();
                        }}
                      >
                        <Box sx={{ paddingTop: '75%', position: 'relative', bgcolor: '#F5F7FA' }}>
                          <Box
                            component="img"
                            src={getImageUrl(relProduct.image)}
                            alt={relProduct.name}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </Box>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {relProduct.name}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={700} color="primary">
                            {formatCurrency(relProduct.discountedPrice || relProduct.price, relProduct.currency)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
});

ProductDetailDialog.displayName = 'ProductDetailDialog';

// ============================================
// WHATSAPP CHAT BUTTON
// ============================================

const WhatsAppChat = React.memo(() => {
  const phoneNumber = process.env.REACT_APP_WHATSAPP_NUMBER || '2348000000000';
  const message = 'Hello! I need assistance with your chemical products.';
  return (
    <Tooltip title="Chat with us on WhatsApp" placement="left">
      <Fab
        color="primary"
        aria-label="whatsapp"
        onClick={() =>
          window.open(
            `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
            '_blank'
          )
        }
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: BRAND_COLORS.whatsapp,
          color: 'white',
          zIndex: 1000,
          '&:hover': {
            bgcolor: '#128C7E',
            transform: 'scale(1.1)',
          },
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(37,211,102,0.7)' },
            '70%': { boxShadow: '0 0 0 15px rgba(37,211,102,0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(37,211,102,0)' },
          },
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 28 }} />
      </Fab>
    </Tooltip>
  );
});

WhatsAppChat.displayName = 'WhatsAppChat';

// ============================================
// FOOTER COMPONENT
// ============================================

const Footer = React.memo(() => {
  const currentYear = new Date().getFullYear();
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#0D0D0D',
        color: 'white',
        mt: 4,
        pt: 5,
        pb: 2,
        borderTop: `4px solid ${BRAND_COLORS.primary}`,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src={logo}
                alt="Chemical Database"
                sx={{
                  height: 45,
                  width: 'auto',
                  borderRadius: 2,
                  border: '2px solid rgba(255,255,255,0.1)',
                  padding: '2px',
                  bgcolor: 'rgba(255,255,255,0.05)',
                }}
              />
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  color: BRAND_COLORS.primaryLight,
                  letterSpacing: 0.5,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                }}
              >
                Chemical Database
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                mb: 2.5,
                lineHeight: 1.8,
                maxWidth: 400,
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
              }}
            >
              Your trusted Database for high-quality verified chemical products. We offer a wide range
              of industrial and laboratory chemicals with guaranteed quality and competitive pricing.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, md: 0 } }}>
              {[
                { icon: <FacebookIcon />, label: 'Facebook', color: '#1877F2' },
                { icon: <TwitterIcon />, label: 'Twitter', color: '#1DA1F2' },
                { icon: <InstagramIcon />, label: 'Instagram', color: '#E4405F' },
                { icon: <YouTubeIcon />, label: 'YouTube', color: '#FF0000' },
                { icon: <WhatsAppIcon />, label: 'WhatsApp', color: '#25D366' },
              ].map((social) => (
                <IconButton
                  key={social.label}
                  aria-label={social.label}
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    width: 38,
                    height: 38,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: 'white',
                      bgcolor: social.color,
                      transform: 'translateY(-3px)',
                      boxShadow: `0 4px 15px ${social.color}40`,
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mb: 2.5,
                color: 'white',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 30,
                  height: 2,
                  bgcolor: BRAND_COLORS.primaryLight,
                  borderRadius: 2,
                },
              }}
            >
              Quick Links
            </Typography>
            <List dense sx={{ p: 0 }}>
              {[
                { text: 'About Us', path: 'https://www.rmrdc.gov.ng/about' },
                { text: 'Contact Us', path: 'https://www.rmrdc.gov.ng/contact' },
                { text: 'News', path: 'https://360.rmrdc.gov.ng/' },
                { text: 'FAQs', path: '#' },
                { text: 'Privacy Policy', path: '#' },
              ].map((item) => (
                <ListItem
                  key={item.text}
                  button
                  component="a"
                  href={item.path}
                  sx={{
                    px: 0,
                    py: 0.5,
                    '&:hover': {
                      color: BRAND_COLORS.primaryLight,
                      '& .MuiListItemText-primary': {
                        color: BRAND_COLORS.primaryLight,
                      },
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiTypography-root': {
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        transition: 'color 0.2s ease',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mb: 2.5,
                color: 'white',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 30,
                  height: 2,
                  bgcolor: BRAND_COLORS.primaryLight,
                  borderRadius: 2,
                },
              }}
            >
              Categories
            </Typography>
            <List dense sx={{ p: 0 }}>
              {[
                { text: 'Pharmaceuticals', icon: '💊' },
                { text: 'Industrial', icon: '🏭' },
                { text: 'Laboratory', icon: '🔬' },
                { text: 'Food Grade', icon: '🍽️' },
                { text: 'Agriculture', icon: '🌾' },
              ].map((item) => (
                <ListItem
                  key={item.text}
                  button
                  sx={{
                    px: 0,
                    py: 0.5,
                    '&:hover': {
                      color: BRAND_COLORS.primaryLight,
                      '& .MuiListItemText-primary': {
                        color: BRAND_COLORS.primaryLight,
                      },
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ fontSize: '0.9rem' }}>
                          {item.icon}
                        </Box>
                        <Box component="span" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                          {item.text}
                        </Box>
                      </Box>
                    }
                    sx={{
                      '& .MuiTypography-root': {
                        color: 'rgba(255,255,255,0.6)',
                        transition: 'color 0.2s ease',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mb: 2.5,
                color: 'white',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 30,
                  height: 2,
                  bgcolor: BRAND_COLORS.primaryLight,
                  borderRadius: 2,
                },
              }}
            >
              Get In Touch
            </Typography>

            <List dense sx={{ p: 0, mb: 2.5 }}>
              {[
                { icon: <LocationOnIcon />, text: '17 Aguiyi Ironsi St, Maitama, Abuja' },
                { icon: <PhoneIcon />, text: '+2348039999130 | +2348039999131' },
                { icon: <EmailIcon />, text: 'info@rmrdc.gov.ng' },
                { icon: <WhatsAppIcon />, text: 'Chat with us on WhatsApp' },
              ].map((item) => (
                <ListItem
                  key={item.text}
                  sx={{
                    px: 0,
                    py: 0.75,
                    gap: 1.5,
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      color: BRAND_COLORS.primaryLight,
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { fontSize: 16 } })}
                  </Box>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiTypography-root': {
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3.5, borderColor: 'rgba(255,255,255,0.06)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            © {currentYear} Chemical Database. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((text) => (
              <Typography
                key={text}
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: BRAND_COLORS.primaryLight,
                  },
                  position: 'relative',
                  '&:not(:last-child)::after': {
                    content: '"|"',
                    position: 'absolute',
                    right: -10,
                    color: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                {text}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'rgba(255,255,255,0.04)',
              px: 2.5,
              py: 1,
              borderRadius: 50,
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                fontWeight: 500,
              }}
            >
              Powered by
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: BRAND_COLORS.primaryLight,
                  animation: 'pulseDot 1.5s ease-in-out infinite',
                  '@keyframes pulseDot': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.3, transform: 'scale(0.6)' },
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: BRAND_COLORS.primaryLight,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primaryLight}, ${BRAND_COLORS.primary})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                e-Neurons Technologies Ltd
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

Footer.displayName = 'Footer';

// ============================================
// PRODUCT SKELETON
// ============================================

const ProductSkeleton = React.memo(() => (
  <Card sx={{ height: '100%', borderRadius: 3 }}>
    <Skeleton variant="rectangular" height={150} />
    <CardContent>
      <Skeleton variant="text" width="60%" height={16} />
      <Skeleton variant="text" width="80%" height={20} />
      <Skeleton variant="text" width="40%" height={16} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Skeleton variant="text" width="40%" height={24} />
      </Box>
    </CardContent>
    <CardActions sx={{ p: 1.5, pt: 0 }}>
      <Skeleton variant="rounded" width="100%" height={32} />
    </CardActions>
  </Card>
));

ProductSkeleton.displayName = 'ProductSkeleton';

// ============================================
// SUPPLIER GROUPED CART
// ============================================

const SupplierGroupedCart = React.memo(({
  cart,
  onUpdateQuantity,
  onRemoveItem,
  onCheckoutSupplier,
  onClearCart,
  onClose,
  isAuthenticated,
}) => {
  const groupedCart = useMemo(() => {
    const groups = {};
    cart.forEach((item) => {
      const supplierId = item.supplier_id || 'unknown';
      const supplierName = item.supplier_name || item.ManufacturerName || 'Supplier';
      if (!groups[supplierId]) {
        groups[supplierId] = {
          supplierId,
          supplierName,
          items: [],
          subtotal: 0,
          itemCount: 0,
        };
      }
      const price = item.discountedPrice || item.price || 0;
      const quantity = item.quantity || 1;
      groups[supplierId].items.push(item);
      groups[supplierId].subtotal += price * quantity;
      groups[supplierId].itemCount += quantity;
    });
    return Object.values(groups);
  }, [cart]);

  const formatPrice = useCallback((amount) => {
    if (!amount) return '₦0.00';
    return `₦${Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum + (item.discountedPrice || item.price || 0) * (item.quantity || 1),
        0
      ),
    [cart]
  );

  const totalItems = useMemo(
    () => cart.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [cart]
  );

  if (cart.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CartIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Your cart is empty
        </Typography>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ mt: 2, borderRadius: 25, textTransform: 'none' }}
        >
          Continue Shopping
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #E8ECF1',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Your Cart ({totalItems} items)
        </Typography>
        <Box>
          <Tooltip title="Clear all items">
            <IconButton
              onClick={onClearCart}
              size="small"
              sx={{ color: BRAND_COLORS.error }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {groupedCart.map((group) => (
          <Paper
            key={group.supplierId}
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid #E8ECF1',
              '&:hover': {
                borderColor: BRAND_COLORS.primary,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              },
            }}
          >
            <Box
              sx={{
                p: 1.5,
                bgcolor: '#F8F9FA',
                borderBottom: '1px solid #E8ECF1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StorefrontIcon sx={{ fontSize: 18, color: BRAND_COLORS.primary }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {group.supplierName}
                </Typography>
                <Chip
                  label={`${group.itemCount} items`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.6rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600} color="primary">
                  {formatPrice(group.subtotal)}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => onCheckoutSupplier(group.supplierId)}
                  sx={{
                    borderRadius: 20,
                    textTransform: 'none',
                    fontSize: '0.65rem',
                    py: 0.5,
                    bgcolor: BRAND_COLORS.primary,
                    '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
                  }}
                  disabled={!isAuthenticated}
                >
                  Checkout
                </Button>
              </Box>
            </Box>
            <Box sx={{ p: 1.5 }}>
              {group.items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    py: 1.5,
                    borderBottom: '1px solid #F5F7FA',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box
                    component="img"
                    src={getImageUrl(item.image)}
                    alt={item.name}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      objectFit: 'cover',
                      flexShrink: 0,
                      bgcolor: '#F5F7FA',
                    }}
                    onError={(e) => {
                      e.target.src = '';
                      e.target.style.display = 'none';
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {item.unit_of_measure || 'kg'}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={700} color="primary">
                      {formatPrice(item.discountedPrice || item.price || 0)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          onUpdateQuantity(item.id, (item.quantity || 1) - 1)
                        }
                        sx={{
                          border: '1px solid #E8ECF1',
                          borderRadius: 1,
                          width: 24,
                          height: 24,
                          '&:hover': {
                            bgcolor: alpha(BRAND_COLORS.primary, 0.05),
                          },
                        }}
                      >
                        <RemoveIcon fontSize="small" sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Typography
                        sx={{
                          minWidth: 24,
                          textAlign: 'center',
                          fontSize: '0.85rem',
                        }}
                      >
                        {item.quantity || 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          onUpdateQuantity(item.id, (item.quantity || 1) + 1)
                        }
                        sx={{
                          border: '1px solid #E8ECF1',
                          borderRadius: 1,
                          width: 24,
                          height: 24,
                          '&:hover': {
                            bgcolor: alpha(BRAND_COLORS.primary, 0.05),
                          },
                        }}
                      >
                        <AddIcon fontSize="small" sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveItem(item.id)}
                        sx={{
                          ml: 'auto',
                          color: BRAND_COLORS.error,
                          '&:hover': {
                            bgcolor: alpha(BRAND_COLORS.error, 0.05),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: '1px solid #E8ECF1',
          bgcolor: '#F8F9FA',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Total ({groupedCart.length} {groupedCart.length === 1 ? 'supplier' : 'suppliers'}
            ):
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary">
            {formatPrice(cartTotal)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {groupedCart.length > 1 && (
            <Button
              variant="outlined"
              fullWidth
              onClick={onClearCart}
              sx={{
                borderRadius: 25,
                textTransform: 'none',
                borderColor: BRAND_COLORS.error,
                color: BRAND_COLORS.error,
                '&:hover': {
                  borderColor: BRAND_COLORS.error,
                  bgcolor: alpha(BRAND_COLORS.error, 0.05),
                },
              }}
            >
              Clear All
            </Button>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={() => onCheckoutSupplier('all')}
            sx={{
              borderRadius: 25,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: BRAND_COLORS.primary,
              '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
            }}
            disabled={!isAuthenticated || cart.length === 0}
          >
            Checkout {groupedCart.length > 1 ? `All (${groupedCart.length} orders)` : 'Now'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

SupplierGroupedCart.displayName = 'SupplierGroupedCart';

// ============================================
// CHECKOUT DIALOG
// ============================================

const CheckoutDialog = React.memo(({
  open,
  onClose,
  onConfirm,
  cartItems,
  cartTotal,
  currency = 'NGN',
  user,
  isProcessing,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    shippingAddress: '',
    billingAddress: '',
    city: '',
    state: '',
    country: 'Nigeria',
    postalCode: '',
    deliveryInstructions: '',
    intendedUse: 'Industrial use',
    additionalNotes: '',
    saveAddress: false,
  });
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (open && user) {
      setFormData((prev) => ({
        ...prev,
        shippingAddress: user?.shipping_address || user?.address || '',
        billingAddress: user?.billing_address || user?.address || '',
        city: user?.city || '',
        state: user?.state || '',
        country: user?.country || 'Nigeria',
        postalCode: user?.postal_code || '',
      }));
    }
  }, [open, user]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.shippingAddress?.trim())
      newErrors.shippingAddress = 'Shipping address is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state?.trim()) newErrors.state = 'State/Province is required';
    if (!formData.country?.trim()) newErrors.country = 'Country is required';
    if (!formData.intendedUse?.trim())
      newErrors.intendedUse = 'Please specify the intended use';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (validateForm()) setActiveStep(1);
    } else {
      handleConfirm();
    }
  };

  const handleBack = () => setActiveStep(0);
  const handleConfirm = () => {
    if (validateForm()) onConfirm(formData);
  };

  const groupedItems = useMemo(() => {
    const groups = {};
    cartItems.forEach((item) => {
      const supplierId = item.supplier_id || 'unknown';
      const supplierName = item.supplier_name || item.ManufacturerName || 'Supplier';
      if (!groups[supplierId]) {
        groups[supplierId] = { supplierId, supplierName, items: [], subtotal: 0 };
      }
      const price = item.discountedPrice || item.price || 0;
      const quantity = item.quantity || 1;
      groups[supplierId].items.push(item);
      groups[supplierId].subtotal += price * quantity;
    });
    return Object.values(groups);
  }, [cartItems]);

  const totalItems = useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [cartItems]
  );

  const steps = [
    {
      label: 'Delivery Address',
      icon: <HomeIcon />,
      content: (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide your delivery details. This information will be shared
            with the supplier.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Shipping Address"
                placeholder="Enter your shipping address"
                value={formData.shippingAddress}
                onChange={handleChange('shippingAddress')}
                error={!!errors.shippingAddress}
                helperText={errors.shippingAddress}
                required
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Billing Address"
                placeholder="Enter your billing address (same as shipping if not specified)"
                value={formData.billingAddress}
                onChange={handleChange('billingAddress')}
                multiline
                rows={2}
                variant="outlined"
                helperText="Leave empty if same as shipping address"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                placeholder="Enter city"
                value={formData.city}
                onChange={handleChange('city')}
                error={!!errors.city}
                helperText={errors.city}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State/Province"
                placeholder="Enter state or province"
                value={formData.state}
                onChange={handleChange('state')}
                error={!!errors.state}
                helperText={errors.state}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                placeholder="Enter country"
                value={formData.country}
                onChange={handleChange('country')}
                error={!!errors.country}
                helperText={errors.country}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                placeholder="Enter postal code"
                value={formData.postalCode}
                onChange={handleChange('postalCode')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Intended Use"
                placeholder="e.g., Manufacturing, Laboratory, Research, etc."
                value={formData.intendedUse}
                onChange={handleChange('intendedUse')}
                error={!!errors.intendedUse}
                helperText={
                  errors.intendedUse ||
                  'This helps suppliers understand your requirements'
                }
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Delivery Instructions (Optional)"
                placeholder="Any special delivery instructions"
                value={formData.deliveryInstructions}
                onChange={handleChange('deliveryInstructions')}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes (Optional)"
                placeholder="Any additional information for the supplier"
                value={formData.additionalNotes}
                onChange={handleChange('additionalNotes')}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.saveAddress}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        saveAddress: e.target.checked,
                      }))
                    }
                    sx={{
                      color: BRAND_COLORS.primary,
                      '&.Mui-checked': {
                        color: BRAND_COLORS.primary,
                      },
                    }}
                  />
                }
                label="Save this address for future orders"
              />
            </Grid>
          </Grid>
        </Box>
      ),
    },
    {
      label: 'Review Order',
      icon: <CheckCircleOutlineIcon />,
      content: (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please review your order details before confirming.
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              borderColor: BRAND_COLORS.primary,
              bgcolor: alpha(BRAND_COLORS.primary, 0.04),
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="primary"
              gutterBottom
            >
              📋 Delivery Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
                fontSize: '0.85rem',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Shipping Address
                </Typography>
                <Typography variant="body2">{formData.shippingAddress}</Typography>
                <Typography variant="body2">
                  {formData.city}, {formData.state}
                </Typography>
                <Typography variant="body2">
                  {formData.country} {formData.postalCode}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Intended Use
                </Typography>
                <Typography variant="body2">{formData.intendedUse}</Typography>
                {formData.deliveryInstructions && (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      Delivery Instructions
                    </Typography>
                    <Typography variant="body2">
                      {formData.deliveryInstructions}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Paper>

          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            🛒 Order Items ({totalItems} items)
          </Typography>

          {groupedItems.map((group) => (
            <Paper
              key={group.supplierId}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                borderColor: '#E8ECF1',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <StorefrontIcon sx={{ fontSize: 16, color: BRAND_COLORS.primary }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {group.supplierName}
                </Typography>
                <Chip
                  label={`${group.items.length} items`}
                  size="small"
                  sx={{ ml: 'auto', height: 20, fontSize: '0.6rem' }}
                />
              </Box>
              {group.items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                    borderBottom: '1px solid #F5F7FA',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography variant="body2">
                    {item.name} × {item.quantity || 1}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(
                      (item.discountedPrice || item.price || 0) *
                        (item.quantity || 1),
                      currency
                    )}
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 1,
                  pt: 1,
                  borderTop: '1px solid #E8ECF1',
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Subtotal
                </Typography>
                <Typography variant="body2" fontWeight={700} color="primary">
                  {formatCurrency(group.subtotal, currency)}
                </Typography>
              </Box>
            </Paper>
          ))}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: alpha(BRAND_COLORS.primary, 0.06),
              borderRadius: 2,
              border: `2px solid ${BRAND_COLORS.primary}`,
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Total Amount
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              {formatCurrency(cartTotal, currency)}
            </Typography>
          </Box>

          <Alert
            severity="info"
            sx={{
              mt: 2,
              borderRadius: 2,
              '& .MuiAlert-icon': { alignItems: 'center' },
            }}
          >
            <Typography variant="body2">
              <strong>Note:</strong> The supplier will receive your delivery
              details and contact you regarding order fulfillment.
            </Typography>
          </Alert>
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Slide}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          maxHeight: '90vh',
          background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryDark})`,
          p: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
            }}
          >
            <CartCheckoutIcon sx={{ fontSize: 24, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
              Checkout
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Complete your order details
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stepper
          activeStep={activeStep}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{ mb: 3 }}
        >
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: activeStep >= index ? BRAND_COLORS.primary : '#E8ECF1',
                      color: activeStep >= index ? 'white' : '#999',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {index === activeStep ? step.icon : index + 1}
                  </Box>
                )}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300 }}>{steps[activeStep].content}</Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            pt: 2,
            borderTop: '1px solid #E8ECF1',
          }}
        >
          <Button
            onClick={onClose}
            disabled={isProcessing}
            sx={{
              borderRadius: 25,
              textTransform: 'none',
              color: 'text.secondary',
            }}
          >
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                disabled={isProcessing}
                sx={{
                  borderRadius: 25,
                  textTransform: 'none',
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isProcessing}
              sx={{
                borderRadius: 25,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: BRAND_COLORS.primary,
                '&:hover': {
                  bgcolor: BRAND_COLORS.primaryDark,
                },
                '&:disabled': {
                  bgcolor: 'grey.300',
                },
              }}
              startIcon={activeStep === steps.length - 1 ? <CheckCircleIcon /> : null}
            >
              {isProcessing ? (
                <CircularProgress size={20} color="inherit" />
              ) : activeStep === steps.length - 1 ? (
                'Confirm Order'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

CheckoutDialog.displayName = 'CheckoutDialog';

// ============================================
// HORIZONTAL CATEGORY NAVIGATION COMPONENT
// ============================================

const HorizontalCategoryNav = React.memo(({
  categories,
  selectedCategory,
  onCategorySelect,
  products,
  classificationCounts,
}) => {
  const [showAll, setShowAll] = useState(false);
  const MAX_VISIBLE = 10;
  const theme = useTheme();

  const visibleCategories = useMemo(() => {
    if (showAll) return categories;
    return categories.slice(0, MAX_VISIBLE);
  }, [categories, showAll]);

  const classificationItems = useMemo(() => {
    const items = [];
    const classifications = ['ACID', 'BASE', 'SALT', 'SOLVENT', 'OXIDIZER', 'SURFACTANT', 'GLYCOL', 'FERTILIZER', 'PHARMACEUTICAL', 'AGRICULTURE', 'WATER_TREATMENT', 'FOOD', 'ELECTRONICS', 'INDUSTRIAL'];
    
    classifications.forEach(key => {
      const count = classificationCounts[key] || 0;
      items.push({
        key,
        label: getClassificationLabel(key),
        icon: getClassificationIcon(key),
        color: getClassificationColor(key),
        count,
      });
    });
    return items;
  }, [classificationCounts]);

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        borderBottom: '1px solid #E8ECF1',
        borderTop: '1px solid #E8ECF1',
        py: { xs: 0.5, sm: 1 },
        px: { xs: 1, sm: 2, md: 3 },
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 3 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: '#D1D5DB',
          borderRadius: 2,
        },
        position: 'sticky',
        top: { xs: 56, sm: 64 },
        zIndex: theme.zIndex.appBar - 1,
        bgcolor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          flexWrap: 'nowrap',
          minWidth: 'max-content',
        }}
      >
        <CategoryNavItem
          selected={selectedCategory === 'all'}
          onClick={() => onCategorySelect('all')}
          color={BRAND_COLORS.primary}
          sx={{
            bgcolor: selectedCategory === 'all' ? BRAND_COLORS.primary : '#F5F7FA',
            color: selectedCategory === 'all' ? '#FFFFFF' : 'text.primary',
            '&:hover': {
              bgcolor: selectedCategory === 'all' ? BRAND_COLORS.primaryDark : '#E8ECF1',
            },
          }}
        >
          <CategoryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: selectedCategory === 'all' ? 600 : 400 }}>
            All Products
          </Typography>
          <Box component="span" className="count" sx={{ opacity: 0.7, fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
            ({products.length})
          </Box>
        </CategoryNavItem>

        {classificationItems.map((item) => (
          <CategoryNavItem
            key={item.key}
            selected={selectedCategory === item.key}
            onClick={() => onCategorySelect(item.key)}
            color={item.color}
            sx={{
              bgcolor: selectedCategory === item.key ? item.color : '#F5F7FA',
              color: selectedCategory === item.key ? '#FFFFFF' : 'text.primary',
              '&:hover': {
                bgcolor: selectedCategory === item.key ? item.color : '#E8ECF1',
              },
            }}
          >
            <Box component="span" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
              {item.icon}
            </Box>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: selectedCategory === item.key ? 600 : 400 }}>
              {item.label}
            </Typography>
            {item.count > 0 && (
              <Box component="span" className="count" sx={{ opacity: 0.7, fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                ({item.count})
              </Box>
            )}
          </CategoryNavItem>
        ))}

        <Box
          sx={{
            width: 1,
            height: 24,
            bgcolor: '#E8ECF1',
            mx: { xs: 0.5, sm: 1 },
            flexShrink: 0,
          }}
        />

        {visibleCategories.map((cat) => {
          const displayName = decodeHtmlEntities(cat.category);
          const color = getCategoryColor(cat.category);
          const isSelected = selectedCategory === cat.category;
          return (
            <CategoryNavItem
              key={cat.category}
              selected={isSelected}
              onClick={() => onCategorySelect(cat.category)}
              color={color}
              sx={{
                bgcolor: isSelected ? color : '#F5F7FA',
                color: isSelected ? '#FFFFFF' : 'text.primary',
                '&:hover': {
                  bgcolor: isSelected ? color : '#E8ECF1',
                },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isSelected ? 'rgba(255,255,255,0.6)' : color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: isSelected ? 600 : 400 }}>
                {displayName}
              </Typography>
              <Box component="span" className="count" sx={{ opacity: 0.7, fontSize: { xs: '0.55rem', sm: '0.65rem' } }}>
                ({cat.count})
              </Box>
            </CategoryNavItem>
          );
        })}

        {categories.length > MAX_VISIBLE && (
          <CategoryNavItem
            selected={false}
            onClick={() => setShowAll(!showAll)}
            sx={{
              bgcolor: '#F5F7FA',
              color: 'text.secondary',
              '&:hover': { bgcolor: '#E8ECF1' },
            }}
          >
            {showAll ? 'Show Less' : `+${categories.length - MAX_VISIBLE} More`}
          </CategoryNavItem>
        )}
      </Box>
    </Box>
  );
});

HorizontalCategoryNav.displayName = 'HorizontalCategoryNav';

// ============================================
// SCROLLING BANNER
// ============================================

const ScrollingBanner = React.memo(() => {
  const bannerText = "🔥 Premium Chemical Products • Quality Assured • Verified by RMRDC • Competitive Access • View,Browse & Contact! 🧪";

  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        bgcolor: alpha(BRAND_COLORS.accent, 0.06),
        border: `1px solid ${alpha(BRAND_COLORS.accent, 0.15)}`,
        borderRadius: 2,
        py: 1.2,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        mb: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 60,
          background: 'linear-gradient(90deg, #F5F7FA 0%, rgba(245,247,250,0) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 60,
          background: 'linear-gradient(270deg, #F5F7FA 0%, rgba(245,247,250,0) 100%)',
          zIndex: 1,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          animation: 'scrollText 25s linear infinite',
          whiteSpace: 'nowrap',
          '@keyframes scrollText': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.85rem' },
            fontWeight: 600,
            color: BRAND_COLORS.accent,
            letterSpacing: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {bannerText}
          <Box component="span" sx={{ mx: 3, color: alpha(BRAND_COLORS.accent, 0.2) }}>✦</Box>
          {bannerText}
          <Box component="span" sx={{ mx: 3, color: alpha(BRAND_COLORS.accent, 0.2) }}>✦</Box>
          {bannerText}
        </Typography>
      </Box>
    </Box>
  );
});

ScrollingBanner.displayName = 'ScrollingBanner';

// ============================================
// MAIN ECOMMERCE STORE COMPONENT
// ============================================

const EcommerceStore = ({ initialCategory = 'all' }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // STATE
  // ============================================

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('popular');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [enquiryDialogOpen, setEnquiryDialogOpen] = useState(false);
  const [enquiryProduct, setEnquiryProduct] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [displayProducts, setDisplayProducts] = useState([]);
  const [isClientSearch, setIsClientSearch] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [supplierSwitchDialog, setSupplierSwitchDialog] = useState({
    open: false,
    currentSupplier: null,
    newSupplier: null,
    currentItems: 0,
    newItems: 0,
    currentTotal: 0,
    newTotal: 0,
  });
  const [pendingNewItem, setPendingNewItem] = useState(null);
  const [cartSupplierId, setCartSupplierId] = useState(null);
  const [classificationCounts, setClassificationCounts] = useState({});
  const [featuredProducts, setFeaturedProducts] = useState([]);

  const ITEMS_PER_PAGE = 28;
  const searchInputRef = useRef(null);
  const productContainerRef = useRef(null);

  // ============================================
  // AUTH CHECK
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        const parsedUser = typeof userData === 'string' ? JSON.parse(userData) : userData;
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
      }
    }
  }, []);

  const decodeHtml = useCallback((text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }, []);

  // ============================================
  // FETCH PRODUCTS
  // ============================================

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let url = API_ENDPOINTS.PRODUCTS.PUBLIC;
      const params = new URLSearchParams();
      params.append('limit', 100);
      params.append('offset', 0);

      const classificationKeys = ['ACID', 'BASE', 'SALT', 'SOLVENT', 'OXIDIZER', 'SURFACTANT', 'GLYCOL', 'FERTILIZER', 'PHARMACEUTICAL', 'AGRICULTURE', 'WATER_TREATMENT', 'FOOD', 'ELECTRONICS', 'INDUSTRIAL'];
      const isClassification = classificationKeys.includes(selectedCategory);

      if (selectedCategory && selectedCategory !== 'all' && !isClassification) {
        const decodedCategory = decodeHtml(selectedCategory);
        params.append('category', sanitizeInput(decodedCategory));
      }

      if (sortBy) params.append('sort', sortBy);

      url += '?' + params.toString();
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      if (result.success) {
        const productsData = (result.data.products || []).map((product) => ({
          ...product,
          supplier_email: product.supplier_email || null,
          supplier_name: product.supplier_name || product.ManufacturerName || 'Supplier',
          classification: classifyProduct(product),
        }));
        
        setProducts(productsData);
        setTotalProducts(result.data.total || 0);
        
        let filteredData = productsData;
        
        if (isClassification && selectedCategory !== 'all') {
          filteredData = productsData.filter(p => p.classification === selectedCategory);
        }
        
        applyClientSearch(filteredData, clientSearchTerm);

        const featured = productsData
          .filter(p => p.discounts > 0 || (p.average_rating || 0) >= 4)
          .slice(0, 5);
        setFeaturedProducts(featured.length > 0 ? featured : productsData.slice(0, 5));

        const counts = {};
        productsData.forEach(p => {
          const key = p.classification || 'UNKNOWN';
          counts[key] = (counts[key] || 0) + 1;
        });
        setClassificationCounts(counts);

        if (result.data.categories) {
          const processedCategories = result.data.categories.map((cat) => ({
            ...cat,
            category: decodeHtml(cat.category),
            displayName: decodeHtml(cat.category),
          }));
          setCategories(processedCategories);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch products');
      }
    } catch (error) {
      logger.error('Error fetching products:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load products.',
        severity: 'error',
      });
      setProducts([]);
      setDisplayProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, sortBy, decodeHtml, clientSearchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ============================================
  // CLIENT-SIDE SEARCH
  // ============================================

  useEffect(() => {
    if (products.length > 0) {
      const classificationKeys = ['ACID', 'BASE', 'SALT', 'SOLVENT', 'OXIDIZER', 'SURFACTANT', 'GLYCOL', 'FERTILIZER', 'PHARMACEUTICAL', 'AGRICULTURE', 'WATER_TREATMENT', 'FOOD', 'ELECTRONICS', 'INDUSTRIAL'];
      const isClassification = classificationKeys.includes(selectedCategory);
      
      let filteredData = products;
      
      if (isClassification && selectedCategory !== 'all') {
        filteredData = products.filter(p => p.classification === selectedCategory);
      }
      
      if (clientSearchTerm) {
        applyClientSearch(filteredData, clientSearchTerm);
      } else {
        setDisplayProducts(filteredData);
        setIsClientSearch(false);
      }
    }
  }, [selectedCategory, products, clientSearchTerm]);

  const applyClientSearch = useCallback((productList, searchTerm) => {
    const trimmedSearch = searchTerm?.trim() || '';
    let filtered = productList;
    
    if (!trimmedSearch) {
      setDisplayProducts(productList);
      setIsClientSearch(false);
      return;
    }
    
    const searchLower = trimmedSearch.toLowerCase();
    filtered = productList.filter((product) => {
      const fields = [
        product.name,
        product.registryNumber,
        product.description,
        product.Synonyms,
        product.ManufacturerName,
        product.productType,
        product.applications,
        product.ChemicalFormula,
        product.Grade,
        product.supplier_name,
        getClassificationLabel(product.classification),
      ].filter(Boolean);
      return fields.some((field) => String(field).toLowerCase().includes(searchLower));
    });
    
    setDisplayProducts(filtered);
    setIsClientSearch(true);
  }, []);

  useEffect(() => {
    if (products.length > 0 && clientSearchTerm) {
      applyClientSearch(products, clientSearchTerm);
    }
  }, [products, clientSearchTerm, applyClientSearch]);

  // ============================================
  // CART OPERATIONS
  // ============================================

  const addToCart = useCallback(
    (product) => {
      if (!isAuthenticated) {
        setPendingAction({ type: 'addToCart', product });
        setAuthDialogOpen(true);
        return;
      }

      if (product.quantity_in_stock === 0) {
        setSnackbar({
          open: true,
          message: `${product.name} is out of stock!`,
          severity: 'warning',
        });
        return;
      }

      const productSupplierId = product.supplier_id || 'unknown';
      const productSupplierName =
        product.supplier_name || product.ManufacturerName || 'Supplier';

      if (cart.length === 0) {
        setCart([{ ...product, quantity: 1 }]);
        setCartSupplierId(productSupplierId);
        setSnackbar({
          open: true,
          message: `${product.name} added to cart!`,
          severity: 'success',
        });
        return;
      }

      const existingItem = cart.find((item) => item.id === product.id);
      if (existingItem) {
        setCart((prevCart) =>
          prevCart.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
          )
        );
        setSnackbar({
          open: true,
          message: `${product.name} quantity updated!`,
          severity: 'success',
        });
        return;
      }

      const currentSupplierId = cart[0]?.supplier_id || 'unknown';
      const currentSupplierName =
        cart[0]?.supplier_name || cart[0]?.ManufacturerName || 'Supplier';

      if (productSupplierId !== currentSupplierId) {
        const currentTotal = cart.reduce((sum, item) => {
          const price = item.discountedPrice || item.price || 0;
          return sum + price * (item.quantity || 1);
        }, 0);

        const newPrice = product.discountedPrice || product.price || 0;

        setPendingNewItem({ ...product, quantity: 1 });
        setSupplierSwitchDialog({
          open: true,
          currentSupplier: currentSupplierName,
          newSupplier: productSupplierName,
          currentItems: cart.length,
          newItems: 1,
          currentTotal: currentTotal,
          newTotal: newPrice,
        });
        return;
      }

      setCart((prevCart) => [...prevCart, { ...product, quantity: 1 }]);
      setSnackbar({
        open: true,
        message: `${product.name} added to cart!`,
        severity: 'success',
      });
    },
    [cart, isAuthenticated]
  );

  const removeFromCart = useCallback(
    (productId) => {
      const product = cart.find((item) => item.id === productId);
      const newCart = cart.filter((item) => item.id !== productId);
      setCart(newCart);
      if (newCart.length === 0) setCartSupplierId(null);
      setSnackbar({
        open: true,
        message: `${product?.name || 'Item'} removed from cart`,
        severity: 'info',
      });
    },
    [cart]
  );

  const updateCartQuantity = useCallback(
    (productId, newQuantity) => {
      if (newQuantity <= 0) {
        removeFromCart(productId);
        return;
      }
      if (newQuantity > 10) {
        setSnackbar({
          open: true,
          message: 'Maximum quantity per item is 10',
          severity: 'warning',
        });
        return;
      }
      setCart((prevCart) =>
        prevCart.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    },
    [removeFromCart]
  );

  const clearCart = useCallback(() => {
    setCart([]);
    setCartSupplierId(null);
    setSnackbar({ open: true, message: 'Cart cleared', severity: 'info' });
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce(
      (total, item) => total + (item.discountedPrice || item.price || 0) * (item.quantity || 1),
      0
    );
  }, [cart]);

  const getCartCount = useCallback(() => {
    return cart.reduce((total, item) => total + (item.quantity || 1), 0);
  }, [cart]);

  // ============================================
  // WISHLIST OPERATIONS
  // ============================================

  const toggleWishlist = useCallback(
    (product) => {
      if (!isAuthenticated) {
        setPendingAction({ type: 'toggleWishlist', product });
        setAuthDialogOpen(true);
        return;
      }

      setWishlist((prev) => {
        const exists = prev.find((item) => item.id === product.id);
        if (exists) {
          setSnackbar({
            open: true,
            message: `${product.name} removed from wishlist`,
            severity: 'info',
          });
          return prev.filter((item) => item.id !== product.id);
        }
        setSnackbar({
          open: true,
          message: `${product.name} added to wishlist!`,
          severity: 'success',
        });
        return [...prev, product];
      });
    },
    [isAuthenticated]
  );

  const isInWishlist = useCallback(
    (productId) => {
      return wishlist.some((item) => item.id === productId);
    },
    [wishlist]
  );

  // ============================================
  // ENQUIRY OPERATIONS
  // ============================================

  const handleEnquiry = useCallback(
    (product) => {
      if (!isAuthenticated) {
        setPendingAction({ type: 'enquiry', product });
        setAuthDialogOpen(true);
        return;
      }
      setEnquiryProduct(product);
      setEnquiryDialogOpen(true);
    },
    [isAuthenticated]
  );

  // ============================================
  // ENQUIRY SUBMIT - Uses Order API
  // ============================================

  const handleEnquirySubmit = useCallback(
    async (formData) => {
      setSnackbar({ open: true, message: 'Sending enquiry...', severity: 'info' });
      
      try {
        const orderData = {
          buyer_id: user?.id || user?.user_id,
          session_id: localStorage.getItem('session_id') || 'session_' + Date.now(),
          items: [{
            product_id: enquiryProduct?.id,
            quantity: 1,
            price: enquiryProduct?.discountedPrice || enquiryProduct?.price || 0,
            discount: enquiryProduct?.discounts || 0,
            supplier_id: enquiryProduct?.supplier_id,
            supplier_name: enquiryProduct?.supplier_name || enquiryProduct?.ManufacturerName,
            supplier_email: enquiryProduct?.supplier_email || '',
          }],
          total_amount: enquiryProduct?.discountedPrice || enquiryProduct?.price || 0,
          delivery_fee: 0,
          delivery_method: 'enquiry',
          payment_method: 'enquiry',
          currency: 'NGN',
          shipping_address: `Enquiry from: ${formData.name}`,
          billing_address: formData.email,
          intended_use: 'Product Enquiry',
          notes: `
Enquiry Details:
----------------
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Product: ${enquiryProduct?.name}
Product ID: ${enquiryProduct?.id}

Message:
${formData.message}
          `.trim(),
          order_reference: `ENQ-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        };

        const response = await fetch(API_ENDPOINTS.ORDERS.CREATE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify(orderData),
        });

        const result = await response.json();

        if (result.success) {
          setSnackbar({
            open: true,
            message: `Enquiry sent successfully! The supplier will contact you soon.`,
            severity: 'success',
          });
          setEnquiryDialogOpen(false);
          setEnquiryProduct(null);
        } else {
          throw new Error(result.message || 'Failed to send enquiry');
        }
      } catch (error) {
        logger.error('Enquiry error:', error);
        setSnackbar({
          open: true,
          message: error.message || 'Failed to send enquiry. Please try again.',
          severity: 'error',
        });
      }
    },
    [enquiryProduct, user]
  );

  // ============================================
  // CHECKOUT OPERATIONS
  // ============================================

  const handleCheckoutSupplier = useCallback(
    (supplierId) => {
      if (!isAuthenticated) {
        setAuthDialogOpen(true);
        return;
      }
      if (cart.length === 0) {
        setSnackbar({
          open: true,
          message: 'Your cart is empty',
          severity: 'warning',
        });
        return;
      }
      setCheckoutData({
        supplierId,
        items:
          supplierId === 'all'
            ? cart
            : cart.filter((item) => (item.supplier_id || 'unknown') === supplierId),
      });
      setCheckoutDialogOpen(true);
    },
    [cart, isAuthenticated]
  );

  const handleCheckoutConfirm = useCallback(
    async (formData) => {
      setIsProcessingOrder(true);
      setCheckoutDialogOpen(false);

      try {
        const itemsToCheckout = checkoutData?.items || cart;
        if (itemsToCheckout.length === 0) throw new Error('No items to checkout');

        const totalAmount = itemsToCheckout.reduce(
          (sum, item) =>
            sum + (item.discountedPrice || item.price || 0) * (item.quantity || 1),
          0
        );

        const orderData = {
          buyer_id: user?.id || user?.user_id,
          session_id: localStorage.getItem('session_id') || 'session_' + Date.now(),
          items: itemsToCheckout.map((item) => ({
            product_id: item.id,
            quantity: item.quantity || 1,
            price: item.discountedPrice || item.price,
            discount: item.discounts || 0,
            supplier_id: item.supplier_id,
            supplier_name: item.supplier_name || item.ManufacturerName,
            supplier_email: item.supplier_email || '',
          })),
          total_amount: totalAmount,
          delivery_fee: 0,
          delivery_method: 'standard',
          payment_method: 'card',
          currency: 'NGN',
          shipping_address: `${formData.shippingAddress}, ${formData.city}, ${formData.state}, ${formData.country}${
            formData.postalCode ? ', ' + formData.postalCode : ''
          }`,
          billing_address: formData.billingAddress || formData.shippingAddress,
          intended_use: formData.intendedUse,
          notes: `Delivery Instructions: ${
            formData.deliveryInstructions || 'None'
          }\nAdditional Notes: ${formData.additionalNotes || 'None'}`,
          order_reference: `ORD-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        };

        const response = await fetch(API_ENDPOINTS.ORDERS.CREATE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify(orderData),
        });

        const result = await response.json();

        if (result.success) {
          const checkedOutIds = new Set(itemsToCheckout.map((item) => item.id));
          setCart((prev) => prev.filter((item) => !checkedOutIds.has(item.id)));
          setCartOpen(false);
          setSnackbar({
            open: true,
            message: `Order #${result.data.order_reference} created successfully! Check your email for details.`,
            severity: 'success',
          });
          setTimeout(() => navigate('/buyer/orders'), 2000);
        } else {
          throw new Error(result.message || 'Failed to create order');
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.message || 'Failed to place order.',
          severity: 'error',
        });
      } finally {
        setIsProcessingOrder(false);
        setCheckoutData(null);
      }
    },
    [checkoutData, cart, user, navigate]
  );

  const handleCheckoutClose = useCallback(() => {
    setCheckoutDialogOpen(false);
    setCheckoutData(null);
  }, []);

  // ============================================
  // SUPPLIER SWITCH HANDLERS
  // ============================================

  const handleSupplierSwitchConfirm = useCallback(() => {
    setSupplierSwitchDialog({
      open: false,
      currentSupplier: null,
      newSupplier: null,
      currentItems: 0,
      newItems: 0,
      currentTotal: 0,
      newTotal: 0,
    });
    setCartOpen(false);
    setCheckoutData({
      supplierId: cart[0]?.supplier_id || 'unknown',
      items: cart,
    });
    setCheckoutDialogOpen(true);
  }, [cart]);

  const handleAddAnyway = useCallback(() => {
    setSupplierSwitchDialog({
      open: false,
      currentSupplier: null,
      newSupplier: null,
      currentItems: 0,
      newItems: 0,
      currentTotal: 0,
      newTotal: 0,
    });
    if (pendingNewItem) {
      setCart((prevCart) => [...prevCart, { ...pendingNewItem, quantity: 1 }]);
      setSnackbar({
        open: true,
        message: `${pendingNewItem.name} added with mixed suppliers!`,
        severity: 'info',
      });
      setPendingNewItem(null);
    }
  }, [pendingNewItem]);

  const handleSupplierSwitchClose = useCallback(() => {
    setSupplierSwitchDialog({
      open: false,
      currentSupplier: null,
      newSupplier: null,
      currentItems: 0,
      newItems: 0,
      currentTotal: 0,
      newTotal: 0,
    });
    setPendingNewItem(null);
  }, []);

  // ============================================
  // AUTH HANDLERS
  // ============================================

  const handleLoginRedirect = useCallback(() => {
    setAuthDialogOpen(false);
    navigate('/RawAuth', { state: { from: window.location.pathname } });
  }, [navigate]);

  const handleSignupRedirect = useCallback(() => {
    setAuthDialogOpen(false);
    navigate('/signup', { state: { from: window.location.pathname } });
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setWishlist([]);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_type');
    localStorage.removeItem('buyer_name');
    localStorage.removeItem('organisation');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('userId');
    localStorage.removeItem('userName');
    localStorage.removeItem('business_verified');
    setAnchorEl(null);
    setSnackbar({
      open: true,
      message: 'Logged out successfully',
      severity: 'info',
    });
  }, []);

  // ============================================
  // NAVIGATION HANDLERS
  // ============================================

  const navigateToDashboard = useCallback(() => navigate('/buyer/dashboard'), [navigate]);
  const navigateToOrders = useCallback(() => navigate('/buyer/orders'), [navigate]);

  // ============================================
  // SEARCH AND FILTER HANDLERS
  // ============================================

  const handleSearch = useCallback(
    (event) => {
      const term = event.target.value;
      setClientSearchTerm(term);
      setPage(1);
      applyClientSearch(products, term);
    },
    [products, applyClientSearch]
  );

  const clearSearch = useCallback(() => {
    setClientSearchTerm('');
    setPage(1);
    setDisplayProducts(products);
    setIsClientSearch(false);
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [products]);

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setPage(1);
    setClientSearchTerm('');
  }, []);

  const handleSortChange = useCallback((event) => {
    setSortBy(event.target.value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
    productContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const openProductDialog = useCallback((product) => {
    setSelectedProduct(product);
    setProductDialogOpen(true);
  }, []);

  const closeProductDialog = useCallback(() => {
    setProductDialogOpen(false);
    setTimeout(() => setSelectedProduct(null), 300);
  }, []);

  // ============================================
  // RENDER PRODUCT CARD
  // ============================================

  const renderProductCard = useCallback(
    (product) => {
      const imageUrl = getImageUrl(product.image);
      const price = product.discountedPrice || product.price;
      const categoryColor = getCategoryColor(product.productType);
      const classificationKey = product.classification || 'UNKNOWN';
      const classificationColor = getClassificationColor(classificationKey);
      const classificationLabel = getClassificationLabel(classificationKey);
      const classificationIcon = getClassificationIcon(classificationKey);

      return (
        <ProductCardStyled key={product.id} onClick={() => openProductDialog(product)}>
          <ProductImage className="product-image">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.name}
                loading="lazy"
                onError={(e) => {
                  e.target.src = '';
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#F5F7FA',
                }}
              >
                <ScienceIcon sx={{ fontSize: 48, color: '#D1D5DB' }} />
              </Box>
            )}
            {product.productType && (
              <Chip
                label={decodeHtmlEntities(product.productType)}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  bgcolor: categoryColor,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.55rem',
                  height: 20,
                  maxWidth: '80%',
                  '& .MuiChip-label': { px: 1, fontSize: '0.55rem' },
                }}
              />
            )}
            <Chip
              icon={<Box component="span" sx={{ fontSize: '0.7rem' }}>{classificationIcon}</Box>}
              label={classificationLabel}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: classificationColor,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.5rem',
                height: 20,
                '& .MuiChip-label': { px: 1, fontSize: '0.55rem' },
              }}
            />
          </ProductImage>
      
<CardContent sx={{ flex: 1, p: 1.5, pb: 0.5 }}>
  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.25 }}>
    {product.ManufacturerName || product.supplier_name || 'Supplier'}
  </Typography>
  <Typography
    variant="subtitle2"
    fontWeight={600}
    sx={{
      mb: 0.5,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      fontSize: '0.8rem',
      lineHeight: 1.3,
      minHeight: 32,
    }}
  >
    {product.name}
  </Typography>
 
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
    <Typography
      variant="caption"
      sx={{
        fontFamily: 'monospace',
        fontWeight: 700,
        fontSize: '0.65rem',
        color: BRAND_COLORS.primaryDark,
        bgcolor: alpha(BRAND_COLORS.primary, 0.08),
        px: 0.8,
        py: 0.2,
        borderRadius: 1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.3,
        letterSpacing: 0.3,
      }}
    >
      <Box component="span" sx={{ fontSize: '0.55rem', opacity: 0.6 }}>HS:</Box>
      {product.hs_code || 'N/A'}
    </Typography>
    {product.registryNumber && (
      <Typography
        variant="caption"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.55rem',
          color: 'text.secondary',
          opacity: 0.6,
        }}
      >
        CAS: {product.registryNumber}
      </Typography>
    )}
  </Box>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
    <Rating value={product.average_rating || 0} precision={0.5} size="small" readOnly />
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
      ({product.total_reviews || 0})
    </Typography>
  </Box>
 
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ fontSize: '0.85rem' }}>
                {formatCurrency(price, product.currency)}
              </Typography> */}
              {product.discounts > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textDecoration: 'line-through', fontSize: '0.65rem' }}
                >
                  {formatCurrency(product.price, product.currency)}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: product.quantity_in_stock > 0 ? BRAND_COLORS.success : BRAND_COLORS.error,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
                  {product.quantity_in_stock > 0 ? 'In Stock' : 'Out of Stock'}
                </Typography>
              </Box>
              {product.unit_of_measure && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
                  {product.unit_of_measure}
                </Typography>
              )}
            </Box>
          </CardContent>
          <CardActions sx={{ p: 1.5, pt: 0, gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={product.quantity_in_stock === 0}
              onClick={(e) => {
                e.stopPropagation();
                handleEnquiry(product);
              }}
              size="small"
              sx={{
                borderRadius: 20,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.7rem',
                py: 0.5,
                bgcolor: BRAND_COLORS.primary,
                '&:hover': { bgcolor: BRAND_COLORS.primaryDark },
              }}
              startIcon={<SendIcon sx={{ fontSize: 14 }} />}
            >
              Send Enquiry
            </Button>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleWishlist(product);
              }}
              sx={{ border: '1px solid #E8ECF1', borderRadius: 2, width: 32, height: 32 }}
            >
              {isInWishlist(product.id) ? (
                <FavoriteIcon sx={{ color: BRAND_COLORS.secondary, fontSize: 16 }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </CardActions>
        </ProductCardStyled>
      );
    },
    [openProductDialog, toggleWishlist, isInWishlist, handleEnquiry]
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  const paginatedProducts = displayProducts.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(displayProducts.length / ITEMS_PER_PAGE);

  if (loading && products.length === 0) {
    return <Preloader text="Loading Store..." size={70} fullScreen />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BRAND_COLORS.background }}>
      {/* App Bar */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: '1px solid #E8ECF1',
        }}
      >
        <Toolbar sx={{ flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, py: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              cursor: 'pointer',
            }}
            onClick={() => {
              setClientSearchTerm('');
              setSelectedCategory('all');
              setPage(1);
            }}
          >
            <img src={logo} alt="Store" style={{ height: 40, width: 'auto' }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: BRAND_COLORS.primary,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Chemical Database
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 300 },
              order: { xs: 3, sm: 0 },
            }}
          >
            <SearchBar elevation={0}>
              <IconButton sx={{ p: '8px', color: 'text.secondary' }}>
                <SearchIcon />
              </IconButton>
              <InputBase
                ref={searchInputRef}
                placeholder="Search chemical products..."
                value={clientSearchTerm}
                onChange={handleSearch}
                sx={{
                  flex: 1,
                  color: 'text.primary',
                  '& ::placeholder': { color: 'text.secondary', opacity: 0.7 },
                }}
              />
              {clientSearchTerm && (
                <IconButton
                  sx={{ p: '8px', color: 'text.secondary' }}
                  onClick={clearSearch}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </SearchBar>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {!isAuthenticated ? (
              <>
                <Button
                  variant="text"
                  onClick={() => navigate('/RawAuth')}
                  sx={{
                    borderRadius: 25,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.primary',
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/signup')}
                  sx={{
                    borderRadius: 25,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: BRAND_COLORS.orange,
                  }}
                >
                  Sign Up
                </Button>
              </>
            ) : (
              <>
                <Tooltip title="Dashboard">
                  <IconButton onClick={navigateToDashboard} sx={{ color: 'text.primary' }}>
                    <DashboardIcon />
                  </IconButton>
                </Tooltip>
             
             
                <Tooltip title={user?.display_name || user?.email || 'User'}>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: BRAND_COLORS.primary,
                        color: 'white',
                        fontSize: 14,
                      }}
                    >
                      {getInitials(user?.display_name || user?.full_name || user?.email)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  PaperProps={{
                    sx: { borderRadius: 16, mt: 1.5, minWidth: 220 },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      {user?.display_name || user?.full_name || user?.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {user?.email}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      navigateToDashboard();
                    }}
                  >
                    <ListItemIcon>
                      <DashboardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Dashboard</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      navigateToOrders();
                    }}
                  >
                   
                 
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout} sx={{ color: BRAND_COLORS.error }}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" sx={{ color: BRAND_COLORS.error }} />
                    </ListItemIcon>
                    <ListItemText>Logout</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Horizontal Category Navigation */}
      <HorizontalCategoryNav
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={handleCategorySelect}
        products={products}
        classificationCounts={classificationCounts}
      />

      {/* Auth Dialog */}
      <AuthRequiredDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        onLogin={handleLoginRedirect}
        onSignup={handleSignupRedirect}
        action={
          pendingAction?.type === 'addToCart'
            ? 'add items to your cart'
            : pendingAction?.type === 'toggleWishlist'
            ? 'save items to your wishlist'
            : pendingAction?.type === 'enquiry'
            ? 'send an enquiry'
            : 'continue'
        }
      />

      {/* Contact Dialog */}
      <ContactDialog
        open={enquiryDialogOpen}
        onClose={() => {
          setEnquiryDialogOpen(false);
          setEnquiryProduct(null);
        }}
        product={enquiryProduct}
        onSubmit={handleEnquirySubmit}
        isAuthenticated={isAuthenticated}
      />

      {/* Product Detail Dialog */}
      <ProductDetailDialog
        open={productDialogOpen}
        product={selectedProduct}
        onClose={closeProductDialog}
        onEnquiry={handleEnquiry}
        isInWishlist={isInWishlist}
        onToggleWishlist={toggleWishlist}
      />

      {/* Checkout Dialog */}
      <CheckoutDialog
        open={checkoutDialogOpen}
        onClose={handleCheckoutClose}
        onConfirm={handleCheckoutConfirm}
        cartItems={checkoutData?.items || cart}
        cartTotal={getCartTotal()}
        currency="NGN"
        user={user}
        isProcessing={isProcessingOrder}
      />

      {/* Supplier Switch Dialog */}
      <Dialog
        open={supplierSwitchDialog.open}
        onClose={handleSupplierSwitchClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #FFFFFF 0%, #F8F9FA 100%)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
          },
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.accent}, ${BRAND_COLORS.primaryDark})`,
            p: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
            }}
          >
            <StorefrontIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            Multiple Suppliers Detected
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
            Your cart contains products from different suppliers
          </Typography>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You're adding a product from a different supplier. For a smoother
              checkout experience, we recommend completing your order with the
              current supplier first.
            </Typography>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 3,
              borderColor: BRAND_COLORS.primary,
              bgcolor: alpha(BRAND_COLORS.primary, 0.04),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VerifiedIcon sx={{ fontSize: 16, color: BRAND_COLORS.primary }} />
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                Current Cart - {supplierSwitchDialog.currentSupplier || 'Supplier A'}
              </Typography>
              <Chip
                label={`${supplierSwitchDialog.currentItems} items`}
                size="small"
                sx={{ ml: 'auto', bgcolor: BRAND_COLORS.primary, color: 'white' }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Complete this order first
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                ₦
                {Number(supplierSwitchDialog.currentTotal || 0).toLocaleString(
                  'en-US',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}
              </Typography>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              borderColor: BRAND_COLORS.accent,
              bgcolor: alpha(BRAND_COLORS.accent, 0.04),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StorefrontIcon sx={{ fontSize: 16, color: BRAND_COLORS.accent }} />
              <Typography variant="subtitle2" fontWeight={600} color="accent">
                New Supplier - {supplierSwitchDialog.newSupplier || 'Supplier B'}
              </Typography>
              <Chip
                label={`${supplierSwitchDialog.newItems} items`}
                size="small"
                sx={{ ml: 'auto', bgcolor: BRAND_COLORS.accent, color: 'white' }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Will be added to a separate order
              </Typography>
              <Typography variant="subtitle2" fontWeight={600} color="accent">
                ₦
                {Number(supplierSwitchDialog.newTotal || 0).toLocaleString(
                  'en-US',
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                )}
              </Typography>
            </Box>
          </Paper>

          <Alert
            severity="info"
            sx={{
              borderRadius: 3,
              '& .MuiAlert-icon': { alignItems: 'center' },
            }}
          >
            <Typography variant="body2">
              <strong>Tip:</strong> You can checkout each supplier separately for
              better order management and tracking.
            </Typography>
          </Alert>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleAddAnyway}
              sx={{
                borderRadius: 25,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#E8ECF1',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: BRAND_COLORS.primary,
                  bgcolor: alpha(BRAND_COLORS.primary, 0.04),
                },
              }}
              startIcon={<AddIcon />}
            >
              Add Anyway
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSupplierSwitchConfirm}
              sx={{
                borderRadius: 25,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: BRAND_COLORS.primary,
                '&:hover': {
                  bgcolor: BRAND_COLORS.primaryDark,
                },
              }}
              startIcon={<ShoppingBagIcon />}
            >
              Checkout Current Supplier
            </Button>
          </Box>
        </DialogContent>

        <IconButton
          onClick={handleSupplierSwitchClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.1)',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Dialog>

      {/* Main Content */}
     <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, sm: 1.5, md: 2 } }}>    {/* Scrolling Banner */}
        <ScrollingBanner />

        {/* Header with stats and sort */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {isClientSearch && clientSearchTerm ? (
              `Showing ${displayProducts.length} of ${products.length} products matching "${clientSearchTerm}"`
            ) : (
              `Showing ${products.length} of ${totalProducts} products`
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={sortBy}
                onChange={handleSortChange}
                displayEmpty
                sx={{
                  borderRadius: 2,
                  bgcolor: 'white',
                }}
              >
                <MenuItem value="popular">Most Popular</MenuItem>
                {/* <MenuItem value="price-low">Price: Low to High</MenuItem>
                <MenuItem value="price-high">Price: High to Low</MenuItem> */}
                <MenuItem value="rating">Highest Rated</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Products Grid */}
        <Box ref={productContainerRef}>
          {loading ? (
            <Grid container spacing={2}>
              {[...Array(8)].map((_, idx) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={idx}>
                  <ProductSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : displayProducts.length === 0 ? (
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                borderRadius: 16,
                background: 'white',
              }}
            >
              <ScienceIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No products found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or search terms
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setClientSearchTerm('');
                  setSelectedCategory('all');
                  setPage(1);
                }}
                sx={{ mt: 2, borderRadius: 25 }}
              >
                Clear Filters
              </Button>
            </Paper>
          ) : (
           <Grid container spacing={1.5}>  {/* Reduced spacing */}
  {paginatedProducts.map((product) => (
    <Grid item xs={6} sm={4} md={3} lg={2} key={product.id} sx={{ display: 'flex' }}>
      {renderProductCard(product)}
    </Grid>
  ))}
</Grid>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 2,
                    fontWeight: 500,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Container>

      {/* Cart Drawer */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            maxWidth: '100%',
            borderRadius: { xs: 0, sm: '16px 0 0 16px' },
          },
        }}
      >
        <SupplierGroupedCart
          cart={cart}
          onUpdateQuantity={updateCartQuantity}
          onRemoveItem={removeFromCart}
          onCheckoutSupplier={handleCheckoutSupplier}
          onClearCart={clearCart}
          onClose={() => setCartOpen(false)}
          isAuthenticated={isAuthenticated}
        />
      </Drawer>

      <WhatsAppChat />
      <Footer />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading overlay */}
      {isProcessingOrder && (
        <Backdrop
          sx={{ color: '#fff', zIndex: theme.zIndex.drawer + 2 }}
          open={true}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" />
            <Typography sx={{ mt: 2 }}>Processing your order...</Typography>
          </Box>
        </Backdrop>
      )}
    </Box>
  );
};

export default EcommerceStore;